import React from 'react';
import { makeStyles } from '@material-ui/core';
import DOMPurify from 'dompurify';
import { isSameMinute } from 'date-fns';
import {
    parseSafeDate,
    formatDateWithTime,
    formatRelative,
} from '@klp/utils/dateTimeFormatter';
import { Typography, Tooltip, KlpModal } from '@klp/design';
import type { IKommentar } from '../services/rtkApi';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(() => ({
    modal: {
        maxWidth: '600px',
    },
    commentDate: {
        cursor: 'pointer',
        paddingLeft: '2.5px',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    editIcon: {
        cursor: 'pointer',
        fontSize: '25px',
        paddingLeft: '5px',

        '&:hover': {
            opacity: 0.7,
        },
    },
    editIconTooltip: {
        fontSize: '20px',
    },
}));

type KommentarDetaljerProps = {
    comment?: IKommentar;
    open: boolean;
    onClose: () => void;
};
export const KommentarDetaljer = ({
    comment,
    open,
    onClose,
}: KommentarDetaljerProps) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const createdDateObj = parseSafeDate(comment?.created);
    const updatedDateObj = parseSafeDate(comment?.oppdatert);

    const isUpdated =
        createdDateObj &&
        updatedDateObj &&
        !isSameMinute(createdDateObj, updatedDateObj);
    const createdDateString = formatDateWithTime(createdDateObj);
    const updatedDateString = formatDateWithTime(updatedDateObj);
    const createdRelative = formatRelative(createdDateObj);

    return (
        <KlpModal
            isOpen={open}
            onClose={onClose}
            titleString={comment?.tittel}
            className={classes.modal}
        >
            <Typography
                variant="body2"
                color="textSecondary"
                gutterBottom={true}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {`${comment?.createdAvBruker?.navn}, `}
                <Tooltip title={createdDateString}>
                    <Typography
                        variant="caption"
                        color="secondary"
                        className={classes.commentDate}
                    >
                        {createdRelative}
                    </Typography>
                </Tooltip>
                {isUpdated && (
                    <Tooltip
                        className={classes.editIconTooltip}
                        title={`${t('comments.edited')} ${
                            comment?.oppdatertAvBruker?.navn
                        }, ${updatedDateString} `}
                    >
                        <Edit className={classes.editIcon} />
                    </Tooltip>
                )}
            </Typography>
            {comment?.tekst && (
                <Typography variant="body1">
                    <span
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(comment?.tekst),
                        }}
                    />
                </Typography>
            )}
        </KlpModal>
    );
};
